import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { api, errText } from "../../lib/admin";
import { Btn, Empty, Loading, Pill, FilterBar, field, fmtDateTime } from "./ui";

const Row = ({ g, onToggle, busy }) => {
  const refunded = g.payment_status === "refunded";
  return (
    <article data-testid={`gift-card-row-${g.code}`} className="bg-white border border-[#e7dfd3] rounded-xl p-5 grid sm:grid-cols-12 gap-4 items-center">
      <div className="sm:col-span-3">
        <p className="font-stencil tracking-[0.2em] text-[#14110F] text-lg" data-testid={`gift-code-${g.code}`}>{g.code}</p>
        <p className="font-serif-d text-3xl font-light text-[#8A6A3A]">€{Math.round(g.amount)}</p>
      </div>
      <div className="sm:col-span-5 text-sm text-[#4f4841] space-y-1">
        <p><span className="text-[#6b625a]">Per</span> <strong className="font-medium text-[#14110F]">{g.recipient_name}</strong> · <span className="text-[#6b625a]">da</span> {g.buyer_name}</p>
        <p className="break-all text-[#6b625a]">{g.buyer_email}</p>
        <p>{g.treatment || "Trattamento a scelta"}</p>
        {g.message && <p className="italic font-serif-d text-base">“{g.message}”</p>}
      </div>
      <div className="sm:col-span-4 flex flex-wrap sm:flex-col sm:items-end gap-2">
        {refunded ? <Pill tone="red">Rimborsato</Pill> : g.redeemed ? <Pill tone="neutral" testId={`gift-status-${g.code}`}>Usato</Pill> : <Pill tone="green" testId={`gift-status-${g.code}`}>Valido</Pill>}
        {!refunded && (
          <Btn variant={g.redeemed ? "ghost" : "dark"} loading={busy} onClick={() => onToggle(g)} data-testid={`gift-toggle-${g.code}`}>
            {g.redeemed ? "Segna come non usato" : "Segna come usato"}
          </Btn>
        )}
        <span className="text-xs text-[#6b625a]">Acquistato {fmtDateTime(g.created_at)}{g.redeemed && g.redeemed_at ? ` · usato ${fmtDateTime(g.redeemed_at)}` : ""}</span>
      </div>
    </article>
  );
};

export const GiftCardsAdmin = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("valid");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/admin/gift-cards").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(errText(e)); setItems([]); });
  }, []);
  const onToggle = async (g) => {
    setBusy(g.code);
    try {
      await api.patch(`/admin/gift-cards/${g.code}`, { redeemed: !g.redeemed });
      setItems((xs) => xs.map((x) => (x.code === g.code ? { ...x, redeemed: !g.redeemed, redeemed_at: !g.redeemed ? new Date().toISOString() : null } : x)));
      toast.success(!g.redeemed ? `Buono ${g.code} segnato come usato` : `Buono ${g.code} di nuovo valido`);
    } catch (e) { toast.error(errText(e)); }
    setBusy("");
  };
  if (!items) return <Loading />;
  const valid = items.filter((g) => g.payment_status === "paid" && !g.redeemed);
  const used = items.filter((g) => g.redeemed);
  const base = filter === "valid" ? valid : filter === "used" ? used : items;
  const term = q.trim().toUpperCase();
  const shown = term ? base.filter((g) => `${g.code} ${g.recipient_name} ${g.buyer_name}`.toUpperCase().includes(term)) : base;
  return (
    <div className="space-y-6" data-testid="admin-gift-cards">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <FilterBar value={filter} onChange={setFilter} testPrefix="gift-filter" options={[["valid", "Validi", valid.length], ["used", "Usati", used.length], ["all", "Tutti", items.length]]} />
        <label className="relative block lg:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9b9188]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice o nome" className={`${field} pl-9`} data-testid="gift-search-input" />
        </label>
      </div>
      {shown.length ? <div className="space-y-3">{shown.map((g) => <Row key={g.code} g={g} onToggle={onToggle} busy={busy === g.code} />)}</div>
        : <Empty testId="gift-empty">Nessun buono regalo in questa sezione.</Empty>}
    </div>
  );
};
